// Phase-104 · Shared hub orders table for MicroHubOrders + MainHubOrders.
// Lists scope-filtered orders. Per-role variants (microhub vs mainhub vs readonly)
// expose different action sets.
//
// Phase ASSIGN-FIX-r8 — when variant="microhub" the page is rendered for
// micro_hub_lead. Previously it loaded orders + agents from admin-only
// endpoints (/admin/orders, /admin/users?role=agent) which silently 403'd
// for the lead role and left the table empty. The lead-scoped endpoints
// have always existed (Phase MHM-5.3 + MHM-6.A) but were never wired in.
// The fix routes microhub callers to /micro-hub/orders + /micro-hub/agents
// AND switches the assign POST to /micro-hub/orders/{tid}/assign-(pickup|
// delivery)-agent so the chain-of-custody audit_log + status_timeline
// entries get written.
import { useEffect, useState } from "react";
import { Loader2, RefreshCw, Truck, MapPin, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import api from "../services/api";
import TodaysSnapshot from "./TodaysSnapshot";

const TRANSITIONS = {
  microhub: ["assigned", "at_hub", "in_transit", "out_for_delivery"],
  mainhub:  ["confirmed", "at_hub", "in_transit", "out_for_delivery"],
};

export default function HubOrdersTable({
  variant,        // "microhub" | "mainhub"
  title,
  subtitle,
  readonly = false,
  iconColor = "text-orange-600",
  iconBg = "bg-orange-50",
  iconBorder = "border-orange-200",
}) {
  const [rows, setRows] = useState([]);
  const [pickupAgents, setPickupAgents] = useState([]);
  const [deliveryAgents, setDeliveryAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // Phase ASSIGN-FIX-r8 — route by variant so the lead doesn't 403 on
      // admin-only sources.
      const isMicro = variant === "microhub";
      const ordersUrl  = isMicro ? "/micro-hub/orders?limit=100" : "/admin/orders?limit=100";
      const [ord, pickAg, delivAg] = await Promise.allSettled([
        api.get(ordersUrl),
        isMicro ? api.get("/micro-hub/agents?capability=pickup")   : Promise.resolve({ data: { rows: [] } }),
        isMicro ? api.get("/micro-hub/agents?capability=delivery") : Promise.resolve({ data: { rows: [] } }),
      ]);
      // Surface API failures in a red banner instead of silently rendering
      // an empty table (that's exactly what hid this bug for weeks).
      const errors = [];
      if (ord.status === "fulfilled") {
        setRows(ord.value.data?.rows || []);
      } else {
        setRows([]);
        errors.push(`orders · ${ord.reason?.response?.status || ord.reason?.message || "fail"}`);
      }
      const flat = (resp) => resp.value.data?.agents || resp.value.data?.rows || resp.value.data?.users || [];
      if (isMicro) {
        if (pickAg.status === "fulfilled")  setPickupAgents(flat(pickAg));
        else                                 errors.push(`pickup agents · ${pickAg.reason?.response?.status || "fail"}`);
        if (delivAg.status === "fulfilled") setDeliveryAgents(flat(delivAg));
        else                                 errors.push(`delivery agents · ${delivAg.reason?.response?.status || "fail"}`);
      }
      if (errors.length) setLoadError(errors.join(" · "));
    } catch (e) {
      setLoadError(e?.message || "Unknown error");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const assign = async (row, role, agent_user_id) => {
    if (readonly || !agent_user_id) return;
    const isMicro = variant === "microhub";
    try {
      if (isMicro) {
        // MHM-6.A path — writes status_timeline + audit_log, defeats the
        // 60s auto-expire sweep via accepted_at stamp.
        const path = role === "pickup_agent"
          ? `/micro-hub/orders/${row.tracking_id}/assign-pickup-agent`
          : `/micro-hub/orders/${row.tracking_id}/assign-delivery-agent`;
        await api.post(path, { agent_user_id });
      } else {
        // Admin / Mainhub dispatcher path — see order_lifecycle.py.
        await api.patch(`/orders/${row.tracking_id}/assign`, { role, agent_user_id });
      }
      toast.success(`${row.tracking_id} · ${role.replace("_", " ")} assigned`);
      load();
    } catch (e) {
      toast.error((typeof e?.response?.data?.detail === "string"
        ? e.response.data.detail : null) || "Assign failed");
    }
  };

  const transition = async (row, new_status) => {
    if (readonly) return;
    try {
      await api.patch(`/orders/${row.tracking_id}/status`, { new_status });
      toast.success(`${row.tracking_id} → ${new_status}`);
      setRows((curr) => curr.map((r) =>
        r.tracking_id === row.tracking_id ? { ...r, status: new_status } : r));
    } catch (e) {
      toast.error((typeof e?.response?.data?.detail === "string"
        ? e.response.data.detail : null) || "Transition failed");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid={`${variant}-orders-page`}>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 ${iconBg} border ${iconBorder} rounded-xl flex items-center justify-center`}>
            <Truck className={`h-5 w-5 ${iconColor}`} />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-['Outfit'] text-slate-900">{title}</h1>
            <p className="text-xs text-slate-500">
              {subtitle}{readonly && <span className="ml-2 px-2 py-0.5 bg-slate-100 rounded-full text-[10px] uppercase tracking-wider text-slate-600">Read-only</span>}
            </p>
          </div>
        </div>
        <Button onClick={load} variant="outline" size="sm" data-testid={`${variant}-refresh`}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      <TodaysSnapshot />

      {loadError && (
        <div className="my-3 flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-4 py-3"
             data-testid={`${variant}-load-error`}>
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="text-sm flex-1">Could not load orders / agents · <span className="font-mono">{loadError}</span></span>
          <Button size="sm" variant="outline" onClick={load} className="border-rose-300 text-rose-700 hover:bg-rose-100"
                  data-testid={`${variant}-load-retry`}>Retry</Button>
        </div>
      )}

      {loading ? (
        <div className="p-12 flex items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-12 bg-white border border-slate-200 rounded-2xl text-center text-slate-400"
             data-testid={`${variant}-empty`}>
          <p>No orders in your scope yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="text-left">
                <th className="px-3 py-3 font-semibold">Tracking</th>
                <th className="px-3 py-3 font-semibold">Route</th>
                <th className="px-3 py-3 font-semibold">Status</th>
                <th className="px-3 py-3 font-semibold">Pickup agent</th>
                {variant === "microhub" && <th className="px-3 py-3 font-semibold">Delivery agent</th>}
                <th className="px-3 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.tracking_id} className="border-t border-slate-100"
                    data-testid={`${variant}-row-${r.tracking_id}`}>
                  <td className="px-3 py-3 font-mono text-xs">{r.tracking_id}</td>
                  <td className="px-3 py-3 text-xs text-slate-600">
                    <div className="flex items-center gap-1 truncate max-w-[260px]">
                      <MapPin className="h-3 w-3 shrink-0" />
                      <span className="truncate">{r.pickup_state || "—"}</span>
                      <span className="mx-1">→</span>
                      <span className="truncate">{r.drop_state || "—"}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700">{r.status}</span>
                  </td>
                  <td className="px-3 py-3 text-xs">
                    {variant === "microhub" && !readonly ? (
                      <select
                        defaultValue={r.assigned_pickup_agent_id || r.pickup_agent_id || ""}
                        onChange={(e) => assign(r, "pickup_agent", e.target.value)}
                        className="rounded-md border border-slate-200 px-2 py-1 text-xs"
                        data-testid={`assign-pickup-${r.tracking_id}`}
                      >
                        <option value="">— Assign —</option>
                        {pickupAgents.map((a) => (
                          <option key={a.user_id || a.id || a._id} value={a.user_id || a.id || a._id}>
                            {a.name || a.email}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span>{r.assigned_pickup_agent_name || r.pickup_agent_name || "—"}</span>
                    )}
                  </td>
                  {variant === "microhub" && (
                    <td className="px-3 py-3 text-xs">
                      {!readonly ? (
                        <select
                          defaultValue={r.assigned_delivery_agent_id || r.delivery_agent_id || ""}
                          onChange={(e) => assign(r, "delivery_agent", e.target.value)}
                          className="rounded-md border border-slate-200 px-2 py-1 text-xs"
                          data-testid={`assign-delivery-${r.tracking_id}`}
                        >
                          <option value="">— Assign —</option>
                          {deliveryAgents.map((a) => (
                            <option key={a.user_id || a.id || a._id} value={a.user_id || a.id || a._id}>
                              {a.name || a.email}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span>{r.assigned_delivery_agent_name || r.delivery_agent_name || "—"}</span>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {TRANSITIONS[variant]
                        .filter((s) => s !== r.status)
                        .slice(0, 2)
                        .map((s) => (
                          <Button
                            key={s}
                            size="sm"
                            variant="outline"
                            disabled={readonly}
                            title={readonly ? "Read-only" : ""}
                            onClick={() => transition(r, s)}
                            className="text-xs h-7 px-2"
                            data-testid={`transition-${s}-${r.tracking_id}`}
                          >
                            → {s}
                          </Button>
                        ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
